import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/database'
import {StateChanges, Actions} from './constants'

// Initialize Firebase
const config = {
  apiKey: "AIzaSyDQDLCIq5syV05gAZTkwwjXMHaQP5x17IA",
  authDomain: "dicta-nakdan.firebaseapp.com",
  databaseURL: "https://dicta-nakdan.firebaseio.com",
  projectId: "dicta-nakdan",
  storageBucket: "dicta-nakdan.appspot.com",
  messagingSenderId: "739874995267"
}
let _appPrefix
let syncFieldsToKey
let syncData
let waitingForFields
export let receiveCallbacks = {}

let firebaseInitResolve = null
export const firebaseInitPromise = new Promise(resolve => firebaseInitResolve = resolve)

/**
 * Sets up Firebase syncing for your app
 * @param store - the app's Vuex store, called by the Firebase code to commit data it receives to the
 * 'account' key in the store.
 * @param appPrefix - we have multiple projects storing data in Firebase. Each project uses saves its data under
 * a different key in the same Firebase database.
 * @param syncFields - an array of fields to sync from Firebase. `permissionToUseData` is always synced and
 * shouldn't be listed in the array.
 * @param modifyReceived - by default, this code stores the data from Firebase in the Vuex store without modification.
 * If some preprocessing is necessary, `modifyReceived` allows callbacks to change the data. It should be an object in
 * which the keys are the names of the fields that get special processing, and the values are functions that take the
 * Firebase version of the data and return the version that should appear in `account.sync`.
 * @param sharedSyncFields - a list of fields that are synced that are shared between all Dicta tools. These don't use
 * the app's prefix in the Firebase database. Example: Dropbox token.
 */
export function initializeFirebase (store, {appPrefix, syncFields, modifyReceived, sharedSyncFields}) {
  _appPrefix = appPrefix
  // create a Map of names of keys to the actual key as stored in Firebase
  // in other words, 'dropbox_token' => 'dropbox_token', 'user_dictionary' => 'morphology/user_dictionary'
  syncFieldsToKey = new Map(
    ['permissionToUseData'].concat(syncFields || [])
      .map(name =>
        [name, (_appPrefix ? _appPrefix + '/' : '') + name]
      )
      .concat((sharedSyncFields || []).map(name => [name, name]))
  )
  // create a Map of keys to Firebase refs
  syncData = new Map(
    Array.from(syncFieldsToKey.keys())
      .map(key => [key, null])
  )
  // a Set of fields that haven't been loaded from Firebase yet
  // used when detecting that all values have been loaded
  waitingForFields = new Set(syncData.keys())
  receiveCallbacks = modifyReceived || {}
  firebase.initializeApp(config)
  firebaseInitResolve()
  firebase.auth().onAuthStateChanged(function (user) {
    if (user) {
      // User is signed in.
      // const displayName = user.displayName
      // var email = user.email
      // var emailVerified = user.emailVerified
      // var photoURL = user.photoURL
      // var isAnonymous = user.isAnonymous
      // var uid = user.uid
      // var providerData = user.providerData
      store.dispatch(Actions.PROCESS_LOGIN)
    } else {
      // User is signed out.
      store.commit(StateChanges.LOGGED_OUT)
    }
  })
}

// called by the login action, this sets up listeners on the various keys provided to `initializeFirebase`
export function createFirebaseRefs (commitFunc) {
  const userId = firebase.auth().currentUser.uid
  for (const keyName of syncData.keys()) {
    syncData.set(keyName, firebase.database().ref(
      '/users/' + userId +
      '/' + syncFieldsToKey.get(keyName)
    ))
    bindFirebaseRef(commitFunc, keyName, syncData.get(keyName))
  }
}

export function firebaseStopSync () {
  for (const keyName of syncData.keys()) {
    if (syncData.get(keyName)) {
      syncData.get(keyName).off()
    }
    syncData.set(keyName, null)
  }
}

// actual callback from Firebase
// updates a list of fields that have been received so that the first time that all of them have arrived from Firebase,
// it can set account.firebaseLoaded to true
function bindFirebaseRef (commitFunc, keyName, ref) {
  ref.on('value', (snapshot) => {
    commitFunc(StateChanges.FIREBASE_RECEIVED_UPDATE, {keyName: keyName, value: snapshot.val()})
    if(waitingForFields.has(keyName)) {
      waitingForFields.delete(keyName)
      if (waitingForFields.size === 0) {
        commitFunc(StateChanges.FIREBASE_LOADED)
      }
    }
  })
}

// store data in Firebase, but only if the Firebase ref is already set up
function baseStore(keyName, data) {
  const ref = syncData.get(_appPrefix + keyName) || syncData.get(keyName)
  if (ref) {
    ref.set(data)
    return true
  }
  return false
}

// store data in Firebase, but if Firebase isn't set up yet, delay until the login finishes
export function firebaseStore (keyName, data) {
  if (!baseStore(keyName, data)) {
    // could be login isn't complete, so wait until it finishes
    const unsubscribe = firebase.auth().onAuthStateChanged(user => {
      if (user) {
        baseStore(keyName, data)
        unsubscribe()
      }
    })
  }
}
